import { isEmpty } from "lodash";
import { LocalStorageKey } from "../enums";

export const jsonToQueryString = (json) => {
    return Object.keys(json)
        .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(json[key]))
        .join('&');
}

export const checkPermission = (permissions) => {
    if (isEmpty(permissions)) return false
    const userData = JSON.parse(localStorage.getItem(LocalStorageKey.USER_DATA))
    return permissions.some(permission => (userData.permissions[permission.module] || []).includes(permission.permission))
}

export const camelToUpperSnake = (camelCaseString) => {
    return camelCaseString
        .replace(/([a-z])([A-Z])/g, '$1_$2') // Insert underscore between lower and upper case letters
        .toUpperCase(); // Convert the entire string to uppercase
}

export const toUpperSnakeCase = (string) => {
    return string
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .toUpperCase();       // Convert to uppercase
}

export const toBoolean = (str) => {
    if (typeof obj === "string") {
        return str.toLowerCase() === "true";
    } else {
        return str
    }
}

export const getError = (obj, key) => {
    const keys = key.split('.'); // Split the dynamic key by '.'
    return keys.reduce((acc, curr) => acc && acc[curr], obj);
}

/**
 * Build a sanitized query object by removing null/undefined/empty values
 * and applying custom formatting to specified keys.
 *
 * @param {Object} input - The object to clean (e.g., filter)
 * @param {Array<{ keys: string[], formatter: Function }>} formatMap - Array of key groups with formatter
 * @returns {Object} - Cleaned and formatted query object
 */
export const formatFilterToQuery = (input = {}, formatMap = []) => {
    const mapped = {};

    for (const [key, value] of Object.entries(input)) {
        if (value === undefined || value === null || value === '') continue;

        let formatted = value;

        for (const item of formatMap) {
            if (item.keys.includes(key)) {
                formatted = item.formatter(value);
                break;
            }
        }

        mapped[key] = formatted;
    }

    return mapped;
};

/**
 * Plays notification sound using a singleton Audio instance.
 * This improves reliability across browser autoplay policies by attempting
 * an initial silent play and unlocking on first user interaction if needed.
 * @param {string} soundPath - Path to the sound file (default: '/ringtone.wav')
 */
let notificationAudio;
let notificationAudioUnlocked = false;

const unlockNotificationAudio = (audio) => {
    const tryUnlock = () => {
        audio.play().then(() => {
            audio.pause();
            audio.currentTime = 0;
            notificationAudioUnlocked = true;
            window.removeEventListener('click', tryUnlock);
            window.removeEventListener('touchstart', tryUnlock);
        }).catch(() => {
            // Still locked; keep listeners active until next interaction
        });
    };

    window.addEventListener('click', tryUnlock);
    window.addEventListener('touchstart', tryUnlock);
};

export const playNotificationSound = (soundPath = '/ringtone.wav') => {
    if (typeof window === 'undefined') return;

    try {
        if (!notificationAudio) {
            notificationAudio = new Audio(soundPath);
            notificationAudio.preload = 'auto';
            notificationAudio.volume = 0.5;
            // Try to unlock immediately (if the user already interacted with the page)
            notificationAudio.play()
                .then(() => {
                    notificationAudio.pause();
                    notificationAudio.currentTime = 0;
                    notificationAudioUnlocked = true;
                })
                .catch(() => {
                    // Play is blocked (autoplay policy). Listen for the next user interaction to unlock.
                    unlockNotificationAudio(notificationAudio);
                });
        } else if (notificationAudio.src && !notificationAudio.src.endsWith(soundPath)) {
            // If a different soundPath is requested later, update the source
            notificationAudio.src = soundPath;
        }

        // Always reset to start so repeated notifications replay reliably
        notificationAudio.currentTime = 0;

        notificationAudio.play().catch(error => {
            // Typical cause: autoplay is blocked; unlock handler will attempt later
            console.warn('Could not play notification sound (may be autoplay-blocked):', error);
        });
    } catch (error) {
        console.warn('Error handling notification sound:', error);
    }
};